import { useNavigate } from "react-router-dom";
import { TrendingUp, MapPin } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const VIC_MARGINAL_SEATS = [
  { seat: "Hawthorn",   holder: "Liberal", margin: 0.9,  swing: "+0.9 to ALP needed",  type: "Liberal High Risk",  color: "#1B4F9E", risk: "high" },
  { seat: "Prahran",    holder: "Greens",  margin: 1.8,  swing: "+1.8 to Liberal needed", type: "Greens Hold Risk", color: "#10B981", risk: "high" },
  { seat: "Gembrook",   holder: "Labor",   margin: 1.2,  swing: "+1.2 to Liberal needed", type: "Labor High Risk",  color: "#CC2529", risk: "high" },
  { seat: "Nepean",     holder: "Liberal", margin: 1.5,  swing: "+1.5 to ALP needed",  type: "Liberal Hold Risk", color: "#1B4F9E", risk: "high" },
  { seat: "Caulfield",  holder: "Liberal", margin: 2.1,  swing: "+2.1 to ALP needed",  type: "Liberal Hold Risk", color: "#1B4F9E", risk: "medium" },
  { seat: "Ripon",      holder: "Labor",   margin: 2.8,  swing: "+2.8 to Liberal needed", type: "Labor Hold",     color: "#CC2529", risk: "medium" },
  { seat: "Eureka",     holder: "Labor",   margin: 3.0,  swing: "+3.0 to Liberal needed", type: "Labor Hold",     color: "#CC2529", risk: "medium" },
  { seat: "Sandringham",holder: "Liberal", margin: 3.5,  swing: "+3.5 to ALP needed",  type: "Liberal Hold",     color: "#1B4F9E", risk: "low" },
];

export function VICMarginalSeatsWidget() {
  const { isDark } = useTheme();

  const surface  = isDark ? "#1E293B" : "#FFFFFF";
  const textColor = isDark ? "#F8FAFC" : "#0F172A";
  const textSec  = isDark ? "#94A3B8" : "#475569";
  const borderC  = isDark ? "rgba(51,65,85,0.55)" : "rgba(226,232,240,0.85)";
  const shadowMd = isDark ? "0 4px 20px -2px rgba(0,0,0,0.35)" : "0 4px 16px -2px rgba(15,23,42,0.07)";
  const subBg    = isDark ? "#0F172A" : "#F8FAFC";

  return (
    <section data-testid="vic-marginal-seats-section">
      <div className="flex items-center gap-2.5 mb-6">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(234,179,8,0.12)", border: "1px solid rgba(234,179,8,0.22)" }}
        >
          <MapPin style={{ width: 18, height: 18, color: "#EAB308" }} />
        </div>
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ color: textColor, fontFamily: "Outfit, sans-serif" }}>
            Marginal Seats to Watch
          </h2>
          <p className="text-sm" style={{ color: textSec }}>
            8 lower house seats that could decide who governs VIC in 2026
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="vic-marginal-seats-grid">
        {VIC_MARGINAL_SEATS.map((seat) => (
          <div
            key={seat.seat}
            className="transition-all duration-200"
            style={{
              background: surface,
              border: `1px solid ${borderC}`,
              borderRadius: 14,
              boxShadow: shadowMd,
              padding: "14px 16px",
            }}
            data-testid={`vic-marginal-seat-${seat.seat.toLowerCase()}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold" style={{ color: textColor, fontFamily: "Outfit, sans-serif" }}>
                    {seat.seat}
                  </span>
                  <span
                    className="text-xs font-semibold px-2 py-0.5"
                    style={{
                      borderRadius: 99,
                      background: `${seat.color}18`,
                      color: seat.color,
                      border: `1px solid ${seat.color}33`,
                    }}
                  >
                    {seat.holder}
                  </span>
                </div>
                <p className="text-xs mt-0.5" style={{ color: textSec }}>{seat.type}</p>
              </div>
              <div style={{ textAlign: "right" }}>
                <span
                  className="text-lg font-black"
                  style={{
                    color: seat.risk === "high" ? "#F43F5E" : seat.risk === "medium" ? "#EAB308" : "#10B981",
                    fontFamily: "Outfit, sans-serif",
                  }}
                >
                  {seat.margin}%
                </span>
                <p className="text-xs" style={{ color: textSec }}>margin</p>
              </div>
            </div>

            <div
              className="w-full h-1.5 rounded-full overflow-hidden mt-2"
              style={{ background: isDark ? "rgba(51,65,85,0.5)" : "#F1F5F9" }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(seat.margin * 10, 100)}%`,
                  background:
                    seat.risk === "high"
                      ? "linear-gradient(90deg, #F43F5E, #FB7185)"
                      : seat.risk === "medium"
                      ? "linear-gradient(90deg, #EAB308, #FDE047)"
                      : "linear-gradient(90deg, #10B981, #34D399)",
                }}
              />
            </div>

            <p className="text-xs mt-2 font-medium" style={{ color: textSec }}>{seat.swing}</p>
          </div>
        ))}
      </div>

      <div
        className="mt-5 p-4 rounded-xl flex items-center gap-3"
        style={{ background: subBg, border: `1px solid ${borderC}` }}
      >
        <TrendingUp style={{ width: 16, height: 16, color: "#EAB308", flexShrink: 0 }} />
        <p className="text-xs" style={{ color: textSec }}>
          Margins based on 2022 VIC election results. Labor currently holds a 56-seat majority in the 88-seat assembly.
          A uniform 4% swing to the Coalition would flip ~6 seats and put Labor at risk.
          Current Feb 2026 polling shows Labor leading 56–44 two-party-preferred.
        </p>
      </div>
    </section>
  );
}
